/* eslint-disable */
import { useState, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { Check, ChevronLeft, Lock, ChevronDown } from 'lucide-react';

const C = {
  cream:'#F7F6EF', charcoal:'#2F3A42', navy:'#445366', mid:'#7A8A96',
  border:'#D8DEE3', gold:'#E4AC49', goldLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF',
  white:'#FFFFFF', red:'#DC3545', redLight:'#FFF0F0',
  skyBlue:'#BCD9E5', skyText:'#3A8FA3',
  teal:'#0D9488', tealLight:'#E6FAF8',
  tagBg:'#EDF3FC', tagText:'#2563EB',
};

// ── Time slots ────────────────────────────────────────────────────────────────
// Brunch: 09:00–14:30, Evening: 17:00–20:30, all in 30-min increments
function buildSlots(startH, startM, endH, endM) {
  const slots = [];
  let h = startH, m = startM;
  while (h < endH || (h === endH && m <= endM)) {
    slots.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
    m += 30;
    if (m >= 60) { m = 0; h++; }
  }
  return slots;
}

const BRUNCH_SLOTS  = buildSlots(9, 0, 14, 30);
const EVENING_SLOTS = buildSlots(17, 0, 20, 30);
const ALL_SLOTS     = [
  { label: '— Brunch —', disabled: true, value: '' },
  ...BRUNCH_SLOTS.map(v  => ({ label: v, value: v, type: 'brunch'  })),
  { label: '— Evening —', disabled: true, value: '' },
  ...EVENING_SLOTS.map(v => ({ label: v, value: v, type: 'evening' })),
];

function timeToType(time) {
  if (BRUNCH_SLOTS.includes(time))  return 'brunch';
  if (EVENING_SLOTS.includes(time)) return 'evening';
  return '';
}

// ── Venues ────────────────────────────────────────────────────────────────────
const VENUES = [
  { id:'Box Jordaan',  label:'Box Sociaal Jordaan',  description:'Lively canal-side venue in the heart of Amsterdam\'s historic Jordaan neighbourhood, ideal for private events and group gatherings.',         address:'Karthuizerstraat, Amsterdam',   mapUrl:'https://www.google.com/maps/place//data=!4m2!3m1!1s0x47c609ccd8815123:0x83cbce17f95f1d45?sa=X&ved=1t:8290&ictx=111' },
  { id:'Box Plantage', label:'Box Sociaal Plantage', description:'The OG Box Sociaal, a cosy and vibey venue in Amsterdam\'s leafy Plantage neighbourhood, ideal for groups and intimate events.', address:'Plantage Middenlaan, Amsterdam', mapUrl:'https://www.google.com/maps/place//data=!4m2!3m1!1s0x47c609a279db9467:0xbb623012a6d9ac3c?sa=X&ved=1t:8290&ictx=111' },
];

// ── Packages ──────────────────────────────────────────────────────────────────
const BRUNCH_FOOD = [
  { id:'brunch_standard', name:'Box Standard',  price:'€25 p.p.', priceNum:25, description:'Fruit platter on arrival, one coffee, tea or juice, and one main from our brunch menu.',                                                                              tags:['Pre-order required 3 days in advance'] },
  { id:'brunch_sweety',   name:'Sociaal Sweety', price:'€30 p.p.', priceNum:30, description:'Fruit platter on arrival, one coffee, tea or juice, one main from our brunch menu, plus one signature buttermilk pancake to share between 3–4 guests.',                tags:['Pre-order required 3 days in advance'] },
];

const BRUNCH_DRINKS = { id:'go_bottomless', name:'Go Bottomless', price:'€30 p.p.', priceNum:30, description:'Classic mimosas, Middelaan mimosas, Bloody Marys, house white and red wine, and brand pilsner. Add to any brunch food package.' };

const STANDING_BITES = [
  { id:'bites_standard', name:'Box Standard Bites', price:'€25 p.p.', priceNum:25,                description:'Crispy avocado crackers with lime and pistachio, feta with filo pastry honey and hazelnuts, kibbeling bites, kimchi tacos, sticky toffee pudding.', tags:['15–70 guests'] },
  { id:'bites_deluxe',   name:'Box Deluxe Bites',   price:'€35 p.p.', priceNum:35,                description:'Everything in Standard, plus smoked salmon with dill and maple chilli wings.',                                                                       tags:['15–70 guests'] },
  { id:'bites_premium',  name:'Box Premium Bites',  price:'€45 p.p.', priceNum:45,                description:'Everything in Deluxe, plus mini Sociaal Standards, sticky toffee pudding and raspberry choux with nuts.',                                            tags:['15–70 guests'] },
];

const GROUP_DINNER = [
  { id:'dinner_classics', name:'Box Classics',     price:'€25 p.p.',      priceNum:25, priceMax:null, description:'Choose a main and dessert from our classic menu. Mains: Sociaal Standard Burger or The Parmy. Desserts: Sticky Toffee Pudding or Raspberry Choux.', tags:['15–60 guests', 'Menu pre-selection required'], preselect:true },
  { id:'dinner_comfort',  name:'Box Comfort Menu', price:'From €32 p.p.', priceNum:32, priceMax:45,   description:'Flexible three-course menu. Starter and main from €37, main and dessert from €32, or full three courses from €45.',                               tags:['15–60 guests', 'Menu pre-selection required'], preselect:true },
  { id:'dinner_chefs',    name:"Chef's Menu",       price:'€55 p.p.',      priceNum:55, priceMax:null, description:'Four courses: Steak Tartar, Burrata, Pappardelle with crispy cod, and Choux Pastry with ice cream, pistachio and white chocolate.',               tags:['15–60 guests', 'Fixed menu'] },
];

const DRINK_PACKAGES = [
  { id:'welcome_drink',     name:'Welcome Drink',     price:'€9 p.p.',        priceNum:9,    pricePerHour:false, description:'A signature cocktail crafted by our bar team for your arrival.' },
  { id:'open_bar_standard', name:'Open Bar Standard', price:'€12.50 p.p./hr', priceNum:12.5, pricePerHour:true,  description:'House wines, beers and limited non-alcoholic drinks.' },
  { id:'open_bar_deluxe',   name:'Open Bar Deluxe',   price:'€17.50 p.p./hr', priceNum:17.5, pricePerHour:true,  description:'Hand-selected natural wines, local craft beers and all non-alcoholic drinks.' },
  { id:'open_bar_premium',  name:'Open Bar Premium',  price:'€27.50 p.p./hr', priceNum:27.5, pricePerHour:true,  description:'Our full drinks menu — all cocktails, wines, beers and non-alcoholic drinks.' },
];

const WHOLE_VENUE = {
  id:'whole_venue', name:'Whole Venue Hire', price:'From €2,000 minimum spend',
  description:'Exclusive use of the entire venue for your event. We don\'t charge a venue hire fee — instead we work on a minimum spend basis, meaning every cent goes directly towards food and drinks. Monday to Thursday from €2,000, Friday to Sunday from €2,500 minimum spend.',
};

// ── Quote ─────────────────────────────────────────────────────────────────────
function buildQuote({ eventType, brunchFood, addBottomless, foodPkg, drinkPkg, wholeVenue, guestCount }) {
  const guests = parseInt(guestCount) || 0;
  if (guests === 0) return null;
  const lines = []; let totalMin = 0; let totalMax = 0; let hasHourly = false;

  function addLine(name, priceNum, priceMax) {
    const min = priceNum * guests; const max = (priceMax || priceNum) * guests;
    lines.push({ name, priceNum, priceMax: priceMax || null, guests, min, max });
    totalMin += min; totalMax += max;
  }

  if (eventType === 'brunch') {
    const f = BRUNCH_FOOD.find(p => p.id === brunchFood);
    if (f) addLine(f.name, f.priceNum, null);
    if (addBottomless && brunchFood) addLine(BRUNCH_DRINKS.name, BRUNCH_DRINKS.priceNum, null);
  }
  if (eventType === 'evening') {
    const allFood = [...STANDING_BITES, ...GROUP_DINNER];
    const f = allFood.find(p => p.id === foodPkg);
    if (f) addLine(f.name, f.priceNum, f.priceMax || null);
    if (drinkPkg && drinkPkg !== 'cash_bar') {
      const d = DRINK_PACKAGES.find(p => p.id === drinkPkg);
      if (d) {
        if (d.pricePerHour) { hasHourly = true; lines.push({ name: d.name, priceNum: d.priceNum, guests, perHour: true }); }
        else addLine(d.name, d.priceNum, null);
      }
    }
    if (drinkPkg === 'cash_bar') lines.push({ name: 'Cash bar', cashBar: true });
  }
  if (wholeVenue) lines.push({ name: 'Whole Venue Hire', wholeVenue: true });
  return { lines, totalMin, totalMax, guests, hasHourly };
}

function fmtEur(n) { return '€' + n.toLocaleString('en-GB', { minimumFractionDigits:0, maximumFractionDigits:0 }); }

function QuoteBlock({ quote }) {
  if (!quote || quote.lines.length === 0) return null;
  const { lines, totalMin, totalMax, guests, hasHourly } = quote;
  const hasRange = totalMax > totalMin;
  return (
    <div style={{ background:C.white, border:`2px solid ${C.gold}`, borderRadius:'12px', padding:'16px 18px', marginBottom:'20px' }}>
      <div style={{ fontSize:'11px', fontWeight:800, color:C.navy, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'12px' }}>Estimated Quote</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'12px' }}>
        {lines.map((line, i) => {
          if (line.cashBar)    return <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'13px' }}><span style={{ color:C.mid }}>Cash bar</span><span style={{ color:C.mid }}>—</span></div>;
          if (line.wholeVenue) return <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'13px' }}><span style={{ color:C.charcoal }}>{line.name}</span><span style={{ color:C.navy, fontWeight:700 }}>€2,000+ min. spend</span></div>;
          if (line.perHour)    return (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', fontSize:'13px', gap:'8px' }}>
              <span style={{ color:C.charcoal }}>{line.name}<span style={{ color:C.mid }}> · {guests} guests × €{line.priceNum}/hr</span></span>
              <span style={{ color:C.mid, fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>per hour</span>
            </div>
          );
          const lineRange = line.priceMax && line.priceMax !== line.priceNum;
          return (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', fontSize:'13px', gap:'8px' }}>
              <span style={{ color:C.charcoal }}>{line.name}<span style={{ color:C.mid }}> · {guests} guests × €{line.priceNum}{lineRange ? `–€${line.priceMax}` : ''}</span></span>
              <span style={{ fontWeight:700, color:C.charcoal, whiteSpace:'nowrap', flexShrink:0 }}>{lineRange ? `${fmtEur(line.min)}–${fmtEur(line.max)}` : fmtEur(line.min)}</span>
            </div>
          );
        })}
      </div>
      {totalMin > 0 && (
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:'10px', display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
          <span style={{ fontSize:'13px', fontWeight:700, color:C.charcoal }}>Estimated total{hasHourly ? ' (excl. bar)' : ''}</span>
          <span style={{ fontSize:'17px', fontWeight:800, color:C.navy }}>{hasRange ? `${fmtEur(totalMin)}–${fmtEur(totalMax)}` : fmtEur(totalMin)}</span>
        </div>
      )}
      {hasHourly && <div style={{ fontSize:'11px', color:C.mid, marginTop:'6px' }}>Bar package total depends on duration — agreed at booking.</div>}
      <div style={{ marginTop:'10px', paddingTop:'10px', borderTop:`1px solid ${C.border}`, fontSize:'11px', color:C.mid, lineHeight:1.6 }}>
        Estimated quote only — final pricing confirmed on booking. A deposit is required to secure your event.
      </div>
    </div>
  );
}

// ── UI primitives ─────────────────────────────────────────────────────────────
function FieldLabel({ children, required }) {
  return <label style={{ display:'block', fontSize:'13px', fontWeight:700, color:C.charcoal, marginBottom:'6px' }}>{children}{required && <span style={{ color:C.red, marginLeft:'3px' }}>*</span>}</label>;
}

function InputField({ label, type='text', value, onChange, placeholder, required, hint }) {
  return (
    <div style={{ marginBottom:'18px' }}>
      <FieldLabel required={required}>{label}</FieldLabel>
      {hint && <div style={{ fontSize:'12px', color:C.mid, marginBottom:'5px' }}>{hint}</div>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required}
        style={{ width:'100%', boxSizing:'border-box', padding:'11px 14px', fontSize:'15px', border:`1.5px solid ${C.border}`, borderRadius:'8px', background:C.white, color:C.charcoal, outline:'none', fontFamily:'inherit' }}
        onFocus={e => e.target.style.borderColor = C.gold} onBlur={e => e.target.style.borderColor = C.border} />
    </div>
  );
}

function TextareaField({ label, value, onChange, placeholder, hint }) {
  return (
    <div style={{ marginBottom:'18px' }}>
      <FieldLabel>{label}</FieldLabel>
      {hint && <div style={{ fontSize:'12px', color:C.mid, marginBottom:'5px' }}>{hint}</div>}
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={4}
        style={{ width:'100%', boxSizing:'border-box', padding:'11px 14px', fontSize:'15px', border:`1.5px solid ${C.border}`, borderRadius:'8px', background:C.white, color:C.charcoal, outline:'none', fontFamily:'inherit', resize:'vertical' }}
        onFocus={e => e.target.style.borderColor = C.gold} onBlur={e => e.target.style.borderColor = C.border} />
    </div>
  );
}

// Time dropdown — 24hr, grouped by brunch/evening
function TimeSelect({ value, onChange }) {
  return (
    <div style={{ marginBottom:'18px' }}>
      <FieldLabel required>Start Time</FieldLabel>
      <div style={{ position:'relative' }}>
        <select value={value} onChange={e => onChange(e.target.value)}
          style={{ width:'100%', boxSizing:'border-box', padding:'11px 36px 11px 14px', fontSize:'15px', border:`1.5px solid ${value ? C.gold : C.border}`, borderRadius:'8px', background:C.white, color: value ? C.charcoal : C.mid, outline:'none', fontFamily:'inherit', appearance:'none', cursor:'pointer' }}>
          <option value="">Select a time</option>
          <optgroup label="Brunch (09:00–14:30)">
            {BRUNCH_SLOTS.map(t  => <option key={t} value={t}>{t}</option>)}
          </optgroup>
          <optgroup label="Evening (17:00–20:30)">
            {EVENING_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
          </optgroup>
        </select>
        <ChevronDown size={15} color={C.mid} style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} />
      </div>
    </div>
  );
}

function Tag({ children }) {
  return <span style={{ fontSize:'10px', fontWeight:700, color:C.tagText, background:C.tagBg, padding:'2px 7px', borderRadius:'20px' }}>{children}</span>;
}

function PackageCard({ pkg, selected, onToggle, accentColor=C.gold, accentLight=C.goldLight, disabled=false }) {
  return (
    <div onClick={() => !disabled && onToggle()}
      style={{ border:`2px solid ${selected ? accentColor : C.border}`, borderRadius:'10px', padding:'14px 16px', cursor: disabled ? 'not-allowed' : 'pointer', background: disabled ? '#FAFAFA' : selected ? accentLight : C.white, transition:'all 0.15s', position:'relative', opacity: disabled ? 0.5 : 1 }}>
      {selected && !disabled && (
        <div style={{ position:'absolute', top:'10px', right:'10px', width:'20px', height:'20px', borderRadius:'50%', background:accentColor, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Check size={11} color={C.white} strokeWidth={3} />
        </div>
      )}
      <div style={{ paddingRight: selected && !disabled ? '28px' : 0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'8px', marginBottom:'4px' }}>
          <div style={{ fontSize:'14px', fontWeight:700, color: disabled ? C.mid : C.charcoal }}>{pkg.name}</div>
          <div style={{ fontSize:'13px', fontWeight:800, color: disabled ? C.mid : C.charcoal, whiteSpace:'nowrap', flexShrink:0 }}>{pkg.price}</div>
        </div>
        <div style={{ fontSize:'13px', color:C.mid, lineHeight:1.55, marginBottom: pkg.tags?.length ? '8px' : 0 }}>{pkg.description}</div>
        {pkg.tags?.length > 0 && (
          <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
            {pkg.tags.map(t => <Tag key={t}>{t}</Tag>)}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontSize:'11px', fontWeight:800, color:C.navy, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'10px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>{children}</div>;
}

function StepDots({ current }) {
  return (
    <div style={{ display:'flex', gap:'5px', alignItems:'center' }}>
      {[0,1,2].map(i => <div key={i} style={{ width: i < current ? '18px' : '6px', height:'6px', borderRadius:'3px', background: i < current ? C.gold : i === current ? C.navy : C.border, transition:'all 0.25s' }} />)}
    </div>
  );
}

function ContinueBtn({ valid, onClick, label='Continue' }) {
  return (
    <>
      <button onClick={() => valid && onClick()}
        style={{ width:'100%', padding:'14px', fontSize:'15px', fontWeight:700, background: valid ? C.gold : C.border, color: valid ? C.charcoal : C.mid, border:'none', borderRadius:'10px', cursor: valid ? 'pointer' : 'not-allowed', transition:'all 0.15s' }}>
        {label}
      </button>
      {!valid && <div style={{ fontSize:'12px', color:C.mid, textAlign:'center', marginTop:'8px' }}>Please complete all required fields to continue</div>}
    </>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function EventEnquiry() {
  const [step, setStep]             = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  // Step 1
  const [venue, setVenue]               = useState('');
  const [eventDate, setEventDate]       = useState('');
  const [eventTime, setEventTime]       = useState('');
  const [guestCount, setGuestCount]     = useState('');
  const [contactName, setContactName]   = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [specialReq, setSpecialReq]     = useState('');

  // Derived from time — no separate eventType state needed
  const eventType = useMemo(() => timeToType(eventTime), [eventTime]);

  // Whole venue
  const [wholeVenue, setWholeVenue] = useState(false);

  // Brunch
  const [brunchFood, setBrunchFood]       = useState('');
  const [addBottomless, setAddBottomless] = useState(false);

  // Evening
  const [foodCategory, setFoodCategory] = useState('');
  const [foodPkg, setFoodPkg]           = useState('');
  const [drinkPkg, setDrinkPkg]         = useState('');

  // Reset package selections when time changes type
  function handleTimeChange(newTime) {
    const newType = timeToType(newTime);
    const oldType = timeToType(eventTime);
    if (newType !== oldType) {
      // Clear all package selections when switching type
      setBrunchFood(''); setAddBottomless(false);
      setFoodCategory(''); setFoodPkg(''); setDrinkPkg('');
    }
    setEventTime(newTime);
  }

  const step1Valid = venue && eventDate && eventTime && guestCount && contactName && contactEmail;
  const step2Valid = eventType || wholeVenue;

  function step3Valid() {
    if (!eventType && wholeVenue) return true;
    if (eventType === 'brunch')  return !!brunchFood;
    if (eventType === 'evening') return !!foodPkg && !!drinkPkg;
    return false;
  }

  function buildPackageStrings() {
    const pkgs = [];
    if (eventType === 'brunch') {
      const f = BRUNCH_FOOD.find(p => p.id === brunchFood);
      if (f) pkgs.push(`${f.name} (${f.price})`);
      if (addBottomless && brunchFood) pkgs.push(`${BRUNCH_DRINKS.name} (${BRUNCH_DRINKS.price})`);
    }
    if (eventType === 'evening') {
      const allFood = [...STANDING_BITES, ...GROUP_DINNER];
      const f = allFood.find(p => p.id === foodPkg);
      if (f) pkgs.push(`${f.name} (${f.price})`);
      if (drinkPkg === 'cash_bar') pkgs.push('Cash bar / no drinks package');
      else { const d = DRINK_PACKAGES.find(p => p.id === drinkPkg); if (d) pkgs.push(`${d.name} (${d.price})`); }
    }
    if (wholeVenue) pkgs.push(`${WHOLE_VENUE.name} (${WHOLE_VENUE.price})`);
    return pkgs;
  }

  const quote = step3Valid()
    ? buildQuote({ eventType, brunchFood, addBottomless, foodPkg, drinkPkg, wholeVenue, guestCount })
    : null;

  async function handleSubmit() {
    if (!step3Valid()) return;
    setSubmitting(true); setError('');
    try {
      const packages = buildPackageStrings();
      const { error: dbErr } = await supabase.from('group_events').insert({
        venue, event_type: eventType || 'whole_venue_only',
        event_date: eventDate, event_time: eventTime,
        guest_count: parseInt(guestCount) || null,
        contact_name: contactName, contact_email: contactEmail,
        contact_phone: contactPhone || null,
        special_requirements: specialReq || null,
        packages, status: 'form_submitted',
        form_submitted_at: new Date().toISOString(),
      });
      if (dbErr) throw new Error(dbErr.message);
      await supabase.functions.invoke('send-event-enquiry-email', {
        body: { type:'form_submitted', eventDetails:{ contactName, contactEmail, venue, eventDate, guestCount, packages } },
      });
      setStep(4);
    } catch(e) {
      setError('Something went wrong — please try again or email us at info@boxsociaal.com');
    } finally { setSubmitting(false); }
  }

  // ── Step 4 ────────────────────────────────────────────────────────────────
  if (step === 4) {
    const pkgs = buildPackageStrings();
    return (
      <div style={{ minHeight:'100vh', background:C.cream, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>
        <div style={{ maxWidth:'480px', width:'100%', textAlign:'center' }}>
          <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:C.greenLight, border:`2px solid ${C.green}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
            <Check size={24} color={C.green} strokeWidth={2.5} />
          </div>
          <h2 style={{ fontSize:'22px', fontWeight:800, color:C.charcoal, margin:'0 0 10px' }}>Enquiry received</h2>
          <p style={{ fontSize:'15px', color:C.mid, lineHeight:1.7, margin:'0 0 24px' }}>
            Thanks {contactName.split(' ')[0]}. We'll be in touch at <strong style={{ color:C.charcoal }}>{contactEmail}</strong> within 24 hours.
          </p>
          <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:'10px', padding:'16px 20px', textAlign:'left', marginBottom:'20px' }}>
            <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'10px' }}>Your enquiry</div>
            <div style={{ fontSize:'14px', color:C.charcoal, display:'flex', flexDirection:'column', gap:'6px' }}>
              <div><span style={{ color:C.mid }}>Venue: </span>{venue}</div>
              <div><span style={{ color:C.mid }}>Date: </span>{new Date(eventDate+'T00:00:00').toLocaleDateString('en-GB',{ weekday:'long', day:'numeric', month:'long', year:'numeric' })} at {eventTime}</div>
              <div><span style={{ color:C.mid }}>Guests: </span>{guestCount}</div>
              {pkgs.length > 0 && <div><span style={{ color:C.mid }}>Packages: </span>{pkgs.join(', ')}</div>}
            </div>
          </div>
          <p style={{ fontSize:'12px', color:C.mid }}><a href="mailto:info@boxsociaal.com" style={{ color:C.navy, fontWeight:600 }}>info@boxsociaal.com</a></p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:C.cream }}>

      {/* Header */}
      <div style={{ background:C.charcoal, padding:'20px 24px 16px' }}>
        <div style={{ maxWidth:'600px', margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:'11px', fontWeight:700, color:C.gold, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'3px' }}>Box Sociaal</div>
            <h1 style={{ fontSize:'20px', fontWeight:800, color:'#FFFFFF', margin:0 }}>Plan Your Group Event</h1>
          </div>
          <StepDots current={step - 1} />
        </div>
      </div>

      {/* Step label */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}` }}>
        <div style={{ maxWidth:'600px', margin:'0 auto', padding:'10px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:'12px', fontWeight:700, color:C.mid }}>
            Step {step} of 3 — {step===1 ? 'Event Details' : step===2 ? 'Type of Event' : 'Package Selection'}
          </span>
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)}
              style={{ display:'flex', alignItems:'center', gap:'4px', background:'none', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:700, color:C.mid, padding:0 }}>
              <ChevronLeft size={13} /> Back
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth:'600px', margin:'0 auto', padding:'24px 24px 80px' }}>

        {/* ── STEP 1 ───────────────────────────────────────────────────────── */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom:'22px' }}>
              <FieldLabel required>Which venue?</FieldLabel>
              <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                {VENUES.map(v => (
                  <div key={v.id} onClick={() => setVenue(v.id)}
                    style={{ border:`2px solid ${venue===v.id ? C.gold : C.border}`, borderRadius:'10px', padding:'14px 16px', background: venue===v.id ? C.goldLight : C.white, cursor:'pointer', transition:'all 0.15s' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'12px' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'14px', fontWeight:700, color:C.charcoal, marginBottom:'3px' }}>{v.label}</div>
                        <div style={{ fontSize:'13px', color:C.mid, lineHeight:1.5, marginBottom:'5px' }}>{v.description}</div>
                        <div style={{ fontSize:'11px', color:C.mid }}>{v.address}</div>
                      </div>
                      <a href={v.mapUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        style={{ fontSize:'11px', fontWeight:700, color:C.navy, background:'#EEF1F5', padding:'5px 10px', borderRadius:'6px', textDecoration:'none', whiteSpace:'nowrap', flexShrink:0 }}>
                        View map
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <InputField label="Event Date" type="date" value={eventDate} onChange={setEventDate} required />
              <TimeSelect value={eventTime} onChange={handleTimeChange} />
            </div>

            {/* Show what type of event the time unlocks */}
            {eventTime && (
              <div style={{ marginTop:'-10px', marginBottom:'16px', padding:'8px 12px', borderRadius:'8px', background: eventType === 'brunch' ? C.goldLight : eventType === 'evening' ? C.tealLight : C.cream, fontSize:'12px', fontWeight:600, color: eventType === 'brunch' ? C.charcoal : eventType === 'evening' ? C.teal : C.mid }}>
                {eventType === 'brunch'  && 'Brunch packages will be available on the next step'}
                {eventType === 'evening' && 'Evening packages will be available on the next step'}
              </div>
            )}

            <InputField label="Number of Guests" type="number" value={guestCount} onChange={setGuestCount} placeholder="e.g. 30" required hint="Approximate is fine at this stage" />

            <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:'20px', marginBottom:'4px' }}>
              <div style={{ fontSize:'13px', fontWeight:700, color:C.charcoal, marginBottom:'14px' }}>Your contact details</div>
              <InputField label="Full Name" value={contactName} onChange={setContactName} placeholder="Your name" required />
              <InputField label="Email Address" type="email" value={contactEmail} onChange={setContactEmail} placeholder="you@example.com" required />
              <InputField label="Phone Number" type="tel" value={contactPhone} onChange={setContactPhone} placeholder="+31 6 ..." />
            </div>

            <TextareaField label="Allergies, dietary requirements or special requests"
              value={specialReq} onChange={setSpecialReq}
              placeholder="Please mention any allergies or dietary requirements here"
              hint="We will follow up on any requirements before your event" />

            <ContinueBtn valid={!!step1Valid} onClick={() => setStep(2)} />
          </div>
        )}

        {/* ── STEP 2 ───────────────────────────────────────────────────────── */}
        {step === 2 && (
          <div>
            <div style={{ fontSize:'16px', fontWeight:700, color:C.charcoal, marginBottom:'6px' }}>
              {eventType === 'brunch'  ? 'Great — your time suits a brunch event' : ''}
              {eventType === 'evening' ? 'Great — your time suits an evening event' : ''}
              {!eventType              ? 'Whole venue hire' : ''}
            </div>
            <div style={{ fontSize:'13px', color:C.mid, marginBottom:'20px' }}>
              {eventType ? 'Confirm your selection and choose whether you\'d like exclusive use of the venue.' : 'Let us know your requirements and we\'ll be in touch.'}
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'28px' }}>

              {/* Brunch option — only shown for brunch times */}
              {eventType === 'brunch' && (
                <div style={{ border:`2px solid ${C.gold}`, borderRadius:'10px', padding:'16px 18px', background:C.goldLight, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ fontSize:'15px', fontWeight:700, color:C.charcoal, marginBottom:'2px' }}>Brunch</div>
                    <div style={{ fontSize:'13px', color:C.mid }}>Weekend brunch packages with optional bottomless drinks</div>
                  </div>
                  <Check size={18} color={C.gold} strokeWidth={2.5} />
                </div>
              )}

              {/* Evening option — only shown for evening times */}
              {eventType === 'evening' && (
                <div style={{ border:`2px solid ${C.gold}`, borderRadius:'10px', padding:'16px 18px', background:C.goldLight, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ fontSize:'15px', fontWeight:700, color:C.charcoal, marginBottom:'2px' }}>Evening Event</div>
                    <div style={{ fontSize:'13px', color:C.mid }}>Group dinner or standing bites, with drinks packages</div>
                  </div>
                  <Check size={18} color={C.gold} strokeWidth={2.5} />
                </div>
              )}

              {/* Whole venue — always available */}
              <div onClick={() => setWholeVenue(p => !p)}
                style={{ border:`2px solid ${wholeVenue ? C.navy : C.border}`, borderRadius:'10px', padding:'16px 18px', cursor:'pointer', background: wholeVenue ? '#EEF1F5' : C.white, transition:'all 0.15s' }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'12px' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'15px', fontWeight:700, color:C.charcoal, marginBottom:'2px' }}>Whole Venue Hire</div>
                    <div style={{ fontSize:'13px', color:C.mid, marginBottom:'8px' }}>Exclusive use of the entire venue for your event</div>
                    <div style={{ fontSize:'12px', color:C.navy, fontWeight:600, background: wholeVenue ? C.white : '#EEF1F5', padding:'7px 10px', borderRadius:'6px', lineHeight:1.5 }}>
                      No venue hire fee — we work on a minimum spend basis, so every cent goes towards food and drinks. Mon–Thu from €2,000 · Fri–Sun from €2,500.
                    </div>
                  </div>
                  {wholeVenue && <Check size={18} color={C.navy} strokeWidth={2.5} style={{ flexShrink:0, marginTop:'2px' }} />}
                </div>
              </div>
            </div>

            <ContinueBtn valid={!!step2Valid} onClick={() => setStep(3)} />
          </div>
        )}

        {/* ── STEP 3 ───────────────────────────────────────────────────────── */}
        {step === 3 && (
          <div>

            {/* Whole venue */}
            {wholeVenue && (
              <div style={{ marginBottom:'24px' }}>
                <SectionTitle>Whole Venue Hire</SectionTitle>
                <div style={{ border:`2px solid ${C.navy}`, borderRadius:'10px', padding:'14px 16px', background:'#EEF1F5' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:'8px', marginBottom:'4px' }}>
                    <div style={{ fontSize:'14px', fontWeight:700, color:C.charcoal }}>{WHOLE_VENUE.name}</div>
                    <div style={{ fontSize:'13px', fontWeight:800, color:C.charcoal, whiteSpace:'nowrap' }}>{WHOLE_VENUE.price}</div>
                  </div>
                  <div style={{ fontSize:'13px', color:C.mid, lineHeight:1.55 }}>{WHOLE_VENUE.description}</div>
                </div>
                {!eventType && (
                  <div style={{ fontSize:'12px', color:C.mid, marginTop:'10px', padding:'10px 14px', background:C.white, border:`1px solid ${C.border}`, borderRadius:'8px' }}>
                    Food and drink packages can be discussed once we connect — our team will follow up after your enquiry.
                  </div>
                )}
              </div>
            )}

            {/* Brunch packages */}
            {eventType === 'brunch' && (
              <>
                <div style={{ marginBottom:'24px' }}>
                  <SectionTitle>Brunch Package</SectionTitle>
                  <div style={{ fontSize:'12px', color:C.mid, marginBottom:'10px' }}>Choose one food package</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                    {BRUNCH_FOOD.map(pkg => (
                      <PackageCard key={pkg.id} pkg={pkg} selected={brunchFood===pkg.id}
                        onToggle={() => { const v = brunchFood===pkg.id ? '' : pkg.id; setBrunchFood(v); if (!v) setAddBottomless(false); }} />
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom:'24px' }}>
                  <SectionTitle>Add Bottomless Drinks?</SectionTitle>
                  {!brunchFood ? (
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 14px', background:'#FAFAFA', border:`1px solid ${C.border}`, borderRadius:'10px' }}>
                      <Lock size={13} color={C.mid} />
                      <span style={{ fontSize:'13px', color:C.mid }}>Select a brunch package above to unlock this option</span>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize:'12px', color:C.mid, marginBottom:'10px' }}>Optional — pairs with either brunch package</div>
                      <PackageCard pkg={BRUNCH_DRINKS} selected={addBottomless} onToggle={() => setAddBottomless(p => !p)} accentColor={C.teal} accentLight={C.tealLight} />
                    </>
                  )}
                </div>
              </>
            )}

            {/* Evening packages */}
            {eventType === 'evening' && (
              <>
                <div style={{ marginBottom:'24px' }}>
                  <SectionTitle>Food</SectionTitle>
                  <div style={{ fontSize:'12px', color:C.mid, marginBottom:'10px' }}>Choose one style — standing bites or a sit-down dinner</div>
                  <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                    {[['bites','Standing Bites'],['dinner','Group Dinner']].map(([key, label]) => (
                      <button key={key} onClick={() => { setFoodCategory(key); setFoodPkg(''); }}
                        style={{ flex:1, padding:'10px', fontSize:'13px', fontWeight:700, border:`2px solid ${foodCategory===key ? C.gold : C.border}`, borderRadius:'8px', cursor:'pointer', background: foodCategory===key ? C.goldLight : C.white, color: foodCategory===key ? C.charcoal : C.mid, transition:'all 0.15s' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {foodCategory === 'bites' && (
                    <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                      {STANDING_BITES.map(pkg => <PackageCard key={pkg.id} pkg={pkg} selected={foodPkg===pkg.id} onToggle={() => setFoodPkg(foodPkg===pkg.id ? '' : pkg.id)} />)}
                    </div>
                  )}
                  {foodCategory === 'dinner' && (
                    <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                      {GROUP_DINNER.map(pkg => <PackageCard key={pkg.id} pkg={pkg} selected={foodPkg===pkg.id} onToggle={() => setFoodPkg(foodPkg===pkg.id ? '' : pkg.id)} />)}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom:'24px' }}>
                  <SectionTitle>Drinks</SectionTitle>
                  {!foodPkg ? (
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 14px', background:'#FAFAFA', border:`1px solid ${C.border}`, borderRadius:'10px' }}>
                      <Lock size={13} color={C.mid} />
                      <span style={{ fontSize:'13px', color:C.mid }}>Select a food package above to unlock drinks options</span>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize:'12px', color:C.mid, marginBottom:'10px' }}>Choose one option</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                        {DRINK_PACKAGES.map(pkg => (
                          <PackageCard key={pkg.id} pkg={pkg} selected={drinkPkg===pkg.id} onToggle={() => setDrinkPkg(drinkPkg===pkg.id ? '' : pkg.id)} accentColor={C.teal} accentLight={C.tealLight} />
                        ))}
                        <div onClick={() => setDrinkPkg(drinkPkg==='cash_bar' ? '' : 'cash_bar')}
                          style={{ border:`2px solid ${drinkPkg==='cash_bar' ? C.navy : C.border}`, borderRadius:'10px', padding:'12px 16px', cursor:'pointer', background: drinkPkg==='cash_bar' ? '#EEF1F5' : C.white, transition:'all 0.15s', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <div>
                            <div style={{ fontSize:'14px', fontWeight:700, color:C.charcoal }}>Cash bar / no drinks package</div>
                            <div style={{ fontSize:'13px', color:C.mid }}>Guests order and pay at the bar</div>
                          </div>
                          {drinkPkg==='cash_bar' && <Check size={16} color={C.navy} strokeWidth={2.5} />}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {quote && <QuoteBlock quote={quote} />}

            {error && <div style={{ background:C.redLight, border:`1px solid ${C.red}`, borderRadius:'8px', padding:'10px 14px', marginBottom:'14px', fontSize:'13px', color:C.red }}>{error}</div>}

            <button onClick={handleSubmit} disabled={submitting || !step3Valid()}
              style={{ width:'100%', padding:'14px', fontSize:'15px', fontWeight:700, background: step3Valid() && !submitting ? C.gold : C.border, color: step3Valid() && !submitting ? C.charcoal : C.mid, border:'none', borderRadius:'10px', cursor: step3Valid() && !submitting ? 'pointer' : 'not-allowed', transition:'all 0.15s' }}>
              {submitting ? 'Sending…' : 'Submit Enquiry'}
            </button>

            <p style={{ fontSize:'12px', color:C.mid, textAlign:'center', marginTop:'12px', lineHeight:1.6 }}>
              A deposit is required to secure your booking. Pre-order required minimum 3 days in advance.{' '}
              <a href="mailto:info@boxsociaal.com" style={{ color:C.navy }}>info@boxsociaal.com</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}